import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info, ExternalLink, Loader2, AlertTriangle, ArrowLeft, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useIsMobile } from '@/hooks/useIsMobile';
import { EmailSubscription } from '@/components/EmailSubscription';

export function AboutSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [license, setLicense] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch('/LICENSE.html');
        if (!response.ok) throw new Error(`Failed to fetch license: ${response.status}`);
        setLicense(await response.text());
      } catch (err) {
        console.error('Failed to fetch license:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch license');
      } finally {
        setIsLoading(false);
      }
    };
    fetchLicense();
  }, []);

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="h-8 px-2 -ml-2 text-muted-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">About AfterHours</h1>
        <div className="text-sm text-muted-foreground mt-3 space-y-2">
          <p>
            AfterHours is a fork of an amazing piece of software called Shakespeare (
            <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="text-foreground hover:underline">
              Shakespeare.diy
            </a>
            ), built by none other than the legendary Alex Gleason and the Soapbox Team.
          </p>
          <p>
            Both are open source explorations of what is possible to accomplish on the nostr protocol.
          </p>
          <p>
            This fork is maintained by{' '}
            <a
              href="https://x21.social/users/npub1r0rs5q2gk0e3dk3nlc7gnu378ec6cnlenqp8a3cjhyzu6f8k5sgs4sq9ac"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:underline"
            >
              @Karnage
            </a>
            .
          </p>
        </div>
      </div>

      <div className="space-y-6 max-w-lg">
        {/* Email Updates */}
        <EmailSubscription />

        {/* Source Code */}
        <Button variant="outline" className="w-full justify-start h-11 text-sm" asChild>
          <a href="https://gitlab.com/soapbox-pub/shakespeare" target="_blank" rel="noopener noreferrer">
            <Code className="h-4 w-4 mr-3" />
            {t('sourceCode')}
            <ExternalLink className="h-4 w-4 ml-auto" />
          </a>
        </Button>

        {/* License */}
        <Accordion type="single" collapsible>
          <AccordionItem value="license-text" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4" />
                <span className="text-sm font-medium">{t('license')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              {isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">{t('loadingLicense')}</span>
                </div>
              ) : error ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t('failedToLoadLicense')}: {error}</AlertDescription>
                </Alert>
              ) : license ? (
                <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: license }} />
              ) : null}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}

export default AboutSettings;
